type CanvasInteractionMode = {
  blueprint: boolean;
  viewOnly: boolean;
  touch: boolean;
};

/** React Flow gesture policy. Touch editors pan with one finger; desktop keeps
 * primary drag available for marquee selection. */
export function canvasPanOnDrag({
  blueprint,
  viewOnly,
  touch,
}: CanvasInteractionMode): boolean | number[] {
  if (blueprint) return false;
  if (viewOnly || touch) return true;
  return [1, 2];
}

export function canvasSelectionOnDrag({
  blueprint,
  viewOnly,
  touch,
}: CanvasInteractionMode): boolean {
  return !blueprint && !viewOnly && !touch;
}

/** Convert a finger-sized screen-space target to React Flow coordinates. */
export function flowHitTolerance(screenPixels: number, zoom: number): number {
  return screenPixels / Math.max(zoom, 0.01);
}

/** Per-side flow-space expansion needed to keep a 14-unit handle at 44px. */
export function touchHandleSlop(zoom: number): number {
  return Math.max(0, (44 / Math.max(zoom, 0.01) - 14) / 2);
}

/** One-sided expansion used perpendicular to the node edge. */
export function touchHandleOutwardSlop(zoom: number): number {
  return Math.max(0, 44 / Math.max(zoom, 0.01) - 14);
}

type SidePlacedTouchTarget = {
  id: string;
  side: "left" | "right" | "top" | "bottom";
  offset: number;
};

/**
 * Cap a terminal's expanded coarse-pointer target at the midpoint to the next
 * terminal on the same edge. This preserves a large outward target without a
 * later-painted neighbour stealing a touch directly over the visible stud.
 */
export function terminalTangentHitCaps(
  terminals: readonly SidePlacedTouchTarget[],
  terminal: SidePlacedTouchTarget,
  nodeWidth: number,
  nodeHeight: number,
  handleSize = 14,
): { before?: number; after?: number } {
  const axisLength =
    terminal.side === "left" || terminal.side === "right"
      ? nodeHeight
      : nodeWidth;
  const center = terminal.offset * axisLength;
  let beforeDistance = Number.POSITIVE_INFINITY;
  let afterDistance = Number.POSITIVE_INFINITY;

  for (const candidate of terminals) {
    if (candidate.id === terminal.id || candidate.side !== terminal.side) {
      continue;
    }
    const delta = candidate.offset * axisLength - center;
    if (delta < 0) beforeDistance = Math.min(beforeDistance, -delta);
    if (delta > 0) afterDistance = Math.min(afterDistance, delta);
    if (delta === 0) {
      beforeDistance = 0;
      afterDistance = 0;
    }
  }

  const cap = (distance: number) =>
    Number.isFinite(distance)
      ? Math.max(0, (distance - handleSize) / 2)
      : undefined;

  return {
    before: cap(beforeDistance),
    after: cap(afterDistance),
  };
}

/** Junction connection handles are presentation-only (cardinal on desktop,
 * explicit branch control on touch); the system model always stores `j`. */
export function storedTerminalId(
  junction: boolean,
  handleId: string,
): string {
  return junction ? "j" : handleId;
}
